import SupplierStatusCell from '@/components/suppliers/SupplierStatusCell';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { SupplierStatus } from '../suppliers/SupplierStatus';
import { Checkbox } from '@/components/ui/checkbox';
import BenefitTypeCell from '@/components/benefits/BenefitTypeCell';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Translator } from '../translator.type';

export type Benefit = {
  id: string;
  code: number;
  name: string;
  type: string;
  supplier: {
    name: string;
  };
  activity?: {
    activityType: string;
  };
  country: string;
  city: string | null;
  updatedAt: string;
  createdBy?: {
    firstname: string;
    lastname: string;
  };
  lastUpdatedBy?: {
    firstname: string;
    lastname: string;
  };
  status: SupplierStatus;
  carRental?: {
    carCategories: {
      id: string;
      model: string;
      category: string;
    }[];
  };
  hotel?: {
    HotelRoomCategory: {
      id: string;
      name: string;
    }[];
  };
  _count: {
    circuitDaysAsStandard: number;
    circuitDaysAsOptional: number;
    circuitDayStepAccommodations: number;
    hotelAssociatedBenefits: number;
  };
};

export const BENEFIT_COLUMNS = (t: Translator): ColumnDef<Benefit>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'code',
    header: t('benefits.columns.code'),
    cell: ({ row }) =>
      row.original.code && (
        <span>{`P${row.original.code?.toString().padStart(4, '0')}`}</span>
      ),
  },
  {
    accessorKey: 'name',
    header: t('benefits.columns.name'),
  },
  {
    accessorKey: 'type',
    header: t('benefits.columns.type'),
    cell: ({ row }) => (
      <BenefitTypeCell
        type={
          row.original.type === 'ACTIVITY' && row.original.activity
            ? row.original.activity?.activityType
            : row.original.type
        }
      />
    ),
  },
  {
    accessorKey: 'supplier.name',
    header: t('benefits.columns.supplier'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis">
            {row.original.supplier.name}
          </TooltipTrigger>
          <TooltipContent>{row.original.supplier.name}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'country',
    header: t('benefits.columns.country'),
  },
  {
    accessorKey: 'city',
    header: t('benefits.columns.city'),
    cell: ({ row }) => <span>{row.original.city ?? '-'}</span>,
  },
  {
    accessorKey: 'updatedAt',
    header: t('benefits.columns.updatedAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'lastUpdatedBy',
    header: t('benefits.columns.lastUpdatedBy'),
    cell: ({ row }) => {
      return row.original.lastUpdatedBy ? (
        <span>
          {row.original.lastUpdatedBy.firstname}{' '}
          {row.original.lastUpdatedBy.lastname}
        </span>
      ) : (
        <span>-</span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: t('benefits.columns.status'),
    cell: ({ row }) => <SupplierStatusCell status={row.original.status} />,
  },
];
